import type { HelpContent } from './entries.tr';
import type { HELP_FAZ8_TR } from './entries.faz8.tr';

type Loc = Pick<HelpContent, 'title' | 'short'>;

/** Phase 8 (Finanzen und Analysen) — Hilfetitel und Kurztexte, Deutsch (ADR-801). Begriffe: docs/I18N_GLOSSARY.md. */
export const HELP_FAZ8_DE: Record<keyof typeof HELP_FAZ8_TR, Loc> = {
  'page.sales_report': {
    title: 'Umsatzbericht',
    short:
      'Bestellungen, Nettoumsatz, durchschnittlicher Warenkorb, Rücksendungen und Stornierungen, neue und wiederkehrende Kunden.',
  },
  'reports.period': {
    title: 'Zeitraum',
    short:
      'Die erfassten Tage (türkische Zeit, Beginn und Ende eingeschlossen). Der Vorzeitraum ist gleich lang.',
  },
  'reports.interval': { title: 'Gruppierung', short: 'Tag, Woche (beginnt am Montag), Monat oder Jahr.' },
  'reports.download': {
    title: 'Datei herunterladen',
    short: 'Lädt den Bericht als CSV oder Excel (XLSX) herunter.',
  },
  'sales_report.store': { title: 'Shop', short: 'Nur Bestellungen des ausgewählten Shops.' },
  'sales_report.channel': {
    title: 'Kanal',
    short: 'Onlineshop, Händlerportal, Bestellung in der Verwaltung oder API.',
  },
  'sales_report.segment': { title: 'Kundentyp', short: 'Bestellungen von Endkunden oder Händlern (Firmen).' },
  'sales_report.summary': {
    title: 'Von brutto zu netto',
    short:
      'Bruttoproduktumsatz minus Rabatte plus Versand ergibt den Bestellwert; abzüglich Rücksendungen und Stornierungen ergibt sich der Nettoumsatz.',
  },
  'sales_report.new_customers': {
    title: 'Neue und wiederkehrende Kunden',
    short:
      'Neu: Die Bestellung ist die erste bestätigte Bestellung dieses Kontos oder dieser E-Mail-Adresse.',
  },
  'dashboard.sales': {
    title: 'Umsatzübersicht',
    short:
      'Nettoumsatz und Bestellungen heute und in den letzten 30 Tagen, verglichen mit gestern und den vorherigen 30 Tagen.',
  },
  'sales_report.breakdown': {
    title: 'Aufschlüsselung',
    short:
      'Nettoumsatz nach Produkt, Variante, Kategorie, Marke, Land, Kanal, Shop, Kundentyp oder Zahlungsart.',
  },
  'page.finance_inputs': {
    title: 'Finanzeingaben',
    short: 'Kosten für die Gewinnberechnung: Zahlungsgebührenregeln, Versandkosten und Betriebsausgaben.',
  },
  'finance_inputs.fee_rule': {
    title: 'Zahlungsgebührenregel',
    short:
      'Satz und Fixgebühr pro Transaktion je Zahlungsart (optional je Ratenanzahl), mit Gültigkeitszeitraum.',
  },
  'finance_inputs.shipment_cost': {
    title: 'Versandkosten',
    short:
      'An den Versanddienstleister gezahlter Betrag für die Sendung (inkl. MwSt.), mit MwSt.-Betrag und Rechnungsnummer.',
  },
  'finance_inputs.import': {
    title: 'Rechnung des Versanddienstleisters hochladen',
    short:
      'Ordnet die Rechnungsdatei des Versanddienstleisters (CSV/Excel) über die Sendungsnummer den Sendungen zu; zuerst erscheint eine Vorschau.',
  },
  'finance_inputs.shipping_rule': {
    title: 'Schätzregel für Versandkosten',
    short: 'Ungefähre Kosten pro Sendung nach Versanddienstleister und Paketgewicht (ohne MwSt.).',
  },
  'finance_inputs.expense': {
    title: 'Betriebsausgabe',
    short: 'Miete, Gehälter, Software, Verpackung usw.; Betrag inkl. MwSt., MwSt. separat.',
  },
  'page.finance_report': {
    title: 'Finanzbericht',
    short: 'Rentabilität, Cashflow, MwSt.-Übersicht, Forderungen; Produkt- und Kanalrentabilität.',
  },
  'finance_report.status': {
    title: 'Datenstand',
    short:
      '„Vollständig“, „Teilweise“ (teils geschätzt oder unbekannt) oder „Keine Daten“ (fehlt; wird nicht als null gezählt).',
  },
  'finance_report.cash': {
    title: 'Cashflow',
    short:
      'Einzahlungen: Zahlungen und Händlerzahlungseingänge; Auszahlungen: Erstattungen, Gebühren, Versanddienstleister, Ausgaben, Werbung.',
  },
  'finance_report.vat': {
    title: 'MwSt.-Übersicht',
    short:
      'MwSt. auf Umsätze im Vergleich zur gezahlten MwSt. auf Ausgaben und Versand. Keine Steuererklärung.',
  },
  'finance_report.products': {
    title: 'Produkt- und Kanalrentabilität',
    short: 'Erlöse, Wareneinsatz und Rohertrag pro Produkt; Deckungsbeitrag pro Kanal.',
  },
  'page.stock_report': {
    title: 'Bestandsbericht',
    short:
      'Bestandswert, Aufschlüsselung nach Lager und Kategorie, Umschlag, Ladenhüter; Übersicht zu MHD und kritischem Bestand.',
  },
  'stock_report.value': {
    title: 'Bestandswert',
    short: 'Einheiten am Lager × aktuelle Kosten (ohne MwSt.); Artikel ohne Kosten werden nicht bewertet.',
  },
  'stock_report.turnover': {
    title: 'Umschlag und Lagerreichweite in Tagen',
    short:
      'Verkaufte Einheiten ÷ durchschnittlicher Bestand; Reichweite in Tagen = Tage des Zeitraums ÷ Umschlag.',
  },
  'stock_report.slow': {
    title: 'Ladenhüter',
    short: 'Auf Lager und seit der gewählten Anzahl von Tagen (Standard: 90) nicht verkauft.',
  },
  'stock_report.warehouse': {
    title: 'Lager',
    short: 'Wenn gewählt, beruhen alle Zahlen nur auf den Bewegungen dieses Lagers.',
  },
  'page.suppliers': {
    title: 'Lieferanten',
    short: 'Firmen, bei denen Sie einkaufen, ihre Salden und ihre Leistung.',
  },
  'page.supplier': { title: 'Lieferant', short: 'Lieferantendaten, Kontoauszug und Bestellungen.' },
  'page.purchase_orders': {
    title: 'Einkauf',
    short: 'Einkaufsanforderungen und bei Lieferanten aufgegebene Bestellungen.',
  },
  'page.purchase_order': {
    title: 'Bestellschein',
    short: 'Positionen, eingegangene und offene Mengen, Wareneingänge.',
  },
  'page.purchase_order_new': {
    title: 'Neue Einkaufsanforderung',
    short: 'Einkaufsanforderung mit Lieferant, Lager und Produkten.',
  },
  'page.goods_receipt': {
    title: 'Wareneingang',
    short: 'Buchen Sie Lieferungen der Lieferanten zur jeweiligen Bestellung in den Bestand ein.',
  },
  'purchasing.code': {
    title: 'Lieferantencode',
    short: 'Kurzer, eindeutiger Code (Buchstaben, Ziffern, Punkt, Bindestrich).',
  },
  'purchasing.payment_terms': {
    title: 'Zahlungsziel',
    short: 'Fälligkeit der Rechnung = Tag des Wareneingangs + diese Anzahl Tage. 0: bar.',
  },
  'purchasing.lead_time': {
    title: 'Lieferzeit',
    short:
      'Erwartete Tage von der Bestellung bis zur Lieferung; dient für das erwartete Datum und die Pünktlichkeitsquote.',
  },
  'purchasing.unit_cost': {
    title: 'Stückkosten und MwSt.',
    short:
      'Stückkosten ohne MwSt. in der Währung der Bestellung; ist die MwSt. leer, gilt die Steuerklasse des Produkts.',
  },
  'purchasing.place_order': {
    title: 'Bestellen',
    short: 'Markiert die Anforderung als bestellt; an den Lieferanten wird keine Nachricht gesendet.',
  },
  'purchasing.receive': {
    title: 'Wareneingang',
    short: 'Die Menge darf die offene Menge nicht übersteigen, außer eine Überlieferung wird angenommen.',
  },
  'purchasing.update_cost': {
    title: 'Kostenaktualisierung',
    short: 'Gleitender gewichteter Durchschnitt aus aktuellem Bestand und eingegangener Ware.',
  },
  'purchasing.ledger_entry': {
    title: 'Kontobuchung',
    short:
      'Zahlungen und Rücksendungen verringern die Verbindlichkeit; Buchungen werden per Gegenbuchung korrigiert.',
  },
  'purchasing.performance': {
    title: 'Lieferantenleistung',
    short: 'Pünktliche Lieferung, Lieferquote, Positionen mit Unter-/Überlieferung, Lieferzeit.',
  },
  'page.report_builder': {
    title: 'Berichtsgenerator',
    short:
      'Erstellen Sie eigene Berichte aus einem Datenbestand, Kennzahlen und Dimensionen; Pivot-Tabelle, Diagramm, Speichern und Teilen.',
  },
  'report_builder.metrics': {
    title: 'Kennzahlen und Dimensionen',
    short: 'Kennzahlen sind summierte Werte; Dimensionen gruppieren sie.',
  },
  'report_builder.pivot': {
    title: 'Pivot-Tabelle',
    short: 'Mit einer Spaltendimension wird jeder Wert zu einer Spalte (eine Kennzahl, bis zu 36 Spalten).',
  },
  'report_builder.share': {
    title: 'Teilen',
    short:
      'Personen, mit denen geteilt wird, sehen die Daten mit ihren eigenen Berechtigungen; nur der Eigentümer kann bearbeiten.',
  },
  'dashboard.widgets': {
    title: 'Berichts-Widgets',
    short:
      'Gespeicherte Berichte als Tabelle, Diagramm oder Summe in Ihrer Übersicht; persönlich, bis zu 12.',
  },
  'reports.print': {
    title: 'Drucken / PDF',
    short:
      'Druckt den Bericht über den Browser; wählen Sie „Als PDF speichern“, um eine PDF-Datei zu erhalten. Menüs und Schaltflächen werden nicht gedruckt.',
  },
  'page.report_files': {
    title: 'Meine Berichtsdateien',
    short:
      'Große, im Hintergrund erstellte Berichtsdateien und geplante Berichte; Dateien werden 30 Tage aufbewahrt.',
  },
  'report_files.background': {
    title: 'Im Hintergrund erstellen',
    short:
      'Erstellt aus dem Bericht im Hintergrund eine Datei mit bis zu 50.000 Zeilen; laden Sie sie unter „Meine Berichtsdateien“ herunter.',
  },
  'report_files.schedule': {
    title: 'Geplanter Bericht',
    short:
      'Versand täglich (Vortag), wöchentlich (Vorwoche, Montag–Sonntag) oder monatlich (Vormonat); nie zweimal pro Zeitraum.',
  },
  'report_files.recipients': {
    title: 'Empfänger',
    short:
      'Nur Mitglieder des Unternehmens; jede Datei wird mit den Berechtigungen des Empfängers erstellt. Die E-Mail enthält nur einen Link.',
  },
  'report_files.enabled': {
    title: 'Versand aktiv',
    short:
      'Eine ausgeschaltete Planung läuft nicht; beim Einschalten wird der nächste Versand neu berechnet.',
  },
  'page.payment_disputes': {
    title: 'Rückbuchungen',
    short:
      'Von Bank oder Zahlungsanbieter gemeldete Rückbuchungen (Chargebacks) und Informationsanfragen: Status, Frist, Dokumente, Auswirkung auf die Finanzen.',
  },
  'page.payment_dispute': {
    title: 'Rückbuchung',
    short: 'Details, Status, Dokumente, Notizen und Verlauf der Rückbuchung.',
  },
  'disputes.status': {
    title: 'Status der Rückbuchung',
    short:
      'Offen → Nachweise eingereicht → Gewonnen oder Verloren; „Akzeptiert“ bedeutet: nicht angefochten. Verlorene/akzeptierte Beträge mindern Gewinn und Cashflow.',
  },
  'disputes.amount': {
    title: 'Strittiger Betrag und Rückbuchungsgebühr',
    short:
      'Der Betrag darf den nicht strittigen Teil der Zahlung nicht übersteigen; die Gebühr fällt unabhängig vom Ergebnis an.',
  },
  'disputes.documents': {
    title: 'Dokument hinzufügen',
    short: 'Fügt Nachweise (PDF, JPEG, PNG; bis 10 MB) als nicht öffentliche Datei hinzu.',
  },
  'page.price_simulation': {
    title: 'Preissimulation',
    short:
      'Zeigt den Gewinn nach Gebühren pro Produkt und für den Zeitraum, wenn sich Preis, Kosten oder Gebühren ändern; Preise werden nicht geändert.',
  },
  'price_simulation.scenario': {
    title: 'Szenario',
    short: 'Ändert Preis und Kosten prozentual (z. B. 10 = Erhöhung, -5 = Senkung).',
  },
  'price_simulation.fee': {
    title: 'Gebührensatz',
    short:
      'Leer: der effektive Gebührensatz der im Zeitraum vereinnahmten Zahlungen (nach Ihren Gebührenregeln).',
  },
  'price_simulation.volume': {
    title: 'Zeitraum für die Absatzmenge',
    short:
      'Im Shop der Preisliste in den letzten 30, 90 oder 365 Tagen einschließlich heute verkaufte Einheiten (abzüglich Stornierungen).',
  },
  'page.marketing_summary': {
    title: 'Marketing- und SEO-Übersicht',
    short:
      'Storefront-Trichter, neue Kunden und CAC, Kundenwert über 12 Monate, Werbung, organische Suche, Kampagnen und Bestellquellen.',
  },
  'marketing_summary.funnel': {
    title: 'Storefront-Trichter',
    short:
      'In den Warenkorb gelegt → Bestellschritt (inkl. ausstehender Zahlung) → bestätigte Bestellung. Aufrufe werden nicht auf dem Server gespeichert.',
  },
  'marketing_summary.cac': {
    title: 'Werbekosten pro Neukunde (CAC)',
    short: 'Gesamte Werbeausgaben im Zeitraum ÷ neue Kunden im Zeitraum.',
  },
  'marketing_summary.value': {
    title: 'Kundenwert (12 Monate)',
    short: 'Nettoumsatz der letzten 365 Tage ÷ Kunden mit Kauf; historisch, keine Prognose.',
  },
  'marketing_summary.sources': {
    title: 'Last-Touch-Quelle',
    short:
      'UTM-Quelle der Storefront-Bestellungen; Bestellungen ohne Cookie-Einwilligung werden separat ausgewiesen.',
  },
  'page.multi_touch': {
    title: 'Multi-Touch-Attribution',
    short:
      'Verteilt bestätigte Storefront-Bestellungen nach den Modellen erster Kontakt, letzter Kontakt, linear und positionsbasiert (U) auf die Quellen.',
  },
  'multi_touch.models': {
    title: 'Attributionsmodelle',
    short:
      'Erster/letzter Kontakt: voller Anteil für diesen Kontakt. Linear: gleiche Anteile. Positionsbasiert (U): je 40 % für ersten und letzten, 20 % für die mittleren Kontaktpunkte.',
  },
  'page.workflows': {
    title: 'Automatisierungsabläufe',
    short:
      'Auslöser → Bedingung → Aktion: Abläufe, die von selbst Bedingungen prüfen und Aktionen ausführen, wenn etwas geschieht.',
  },
  'page.workflow': {
    title: 'Automatisierungsablauf',
    short: 'Auslöser, Bedingungen und Aktionen des Ablaufs; Testen, Einschalten/Anhalten und Laufprotokoll.',
  },
  'page.workflow_run': {
    title: 'Automatisierungslauf',
    short:
      'Wie ein Ereignis verarbeitet wurde: Ereignisfelder, Ergebnisse der Bedingungen und alle Versuche jeder Aktion.',
  },
  'workflows.templates': {
    title: 'Fertige Beispiele',
    short:
      '10 Beispielabläufe: 8 werden hier als Entwurf geöffnet; die 2, die Kunden Nachrichten senden, laufen in ihren eigenen Bereichen.',
  },
  'workflows.linked': {
    title: 'Beispiele für Kundennachrichten',
    short:
      'Warenkorb-Erinnerungen und Wieder-verfügbar-Hinweise gehen an Kunden und werden wegen Einwilligung, İYS und Häufigkeitsgrenzen in ihren eigenen Bereichen verwaltet.',
  },
  'workflows.trigger': {
    title: 'Auslöser',
    short:
      'Das Ereignis, das den Ablauf startet. Ereignisse werden jede Minute abgerufen; Ereignisse vor dem Einschalten werden nicht verarbeitet, dasselbe Ereignis nie zweimal.',
  },
  'workflows.conditions': {
    title: 'Bedingungen',
    short:
      'Vergleiche mit den Ereignisfeldern (höchstens 10). Ein nicht passendes Ereignis wird als „übersprungen“ protokolliert.',
  },
  'workflows.actions': {
    title: 'Aktionen',
    short:
      'Laufen der Reihe nach (höchstens 8) mit den Berechtigungen der Person, die den Ablauf eingeschaltet hat. Eine übersprungene Aktion blockiert die nächste nicht.',
  },
  'workflows.recipients': {
    title: 'Empfänger der Benachrichtigung',
    short:
      'Nur Mitglieder der Verwaltung (höchstens 20). Bei „Alle mit dieser Berechtigung“ zählt die Berechtigung zum Versandzeitpunkt, bei Shop-Ereignissen auch die Shop-Beschränkung.',
  },
  'workflows.limits': {
    title: 'Grenzen',
    short:
      'Wartezeit: Ereignisse innerhalb dieser Zeit nach einem Lauf werden übersprungen. Wird das stündliche Limit überschritten, hält sich der Ablauf selbst an und die Leitung erhält eine E-Mail.',
  },
  'workflows.test': {
    title: 'Mit letzten Ereignissen testen',
    short:
      'Prüft die Bedingungen ohne Speichern mit den letzten 5 Ereignissen des Auslösers. Es läuft keine Aktion.',
  },
  'workflows.pause_all': {
    title: 'Alle anhalten',
    short: 'Notstopp: Alle aktiven Abläufe werden angehalten und ausstehende Läufe abgebrochen.',
  },
  'workflows.run_as': {
    title: 'Person, die den Ablauf eingeschaltet hat',
    short:
      'Aktionen laufen mit den AKTUELLEN Berechtigungen dieser Person; der Ablauf hält sich selbst an, wenn die Mitgliedschaft endet oder eine benötigte Berechtigung wegfällt.',
  },
  'workflows.runs': {
    title: 'Laufprotokoll',
    short:
      'Jedes Ereignis ist ein Lauf: ausstehend, läuft, erfolgreich, fehlgeschlagen, übersprungen oder abgebrochen. Die Details zeigen jeden Aktionsversuch.',
  },
  'workflows.retry': {
    title: 'Wiederholen und abbrechen',
    short:
      'Ein fehlgeschlagener Lauf wird ab der Aktion wiederholt, an der er stehen blieb (der Ablauf muss eingeschaltet sein); ein ausstehender Lauf kann abgebrochen werden.',
  },
};
